import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { formValueSelector } from 'redux-form';
import moment from 'moment';
import _ from 'lodash';
import WeddingParty from '../../PublicWebsite/pages/WeddingParty';
import Poi from '../../PublicWebsite/pages/Poi';
import Faq from '../../PublicWebsite/pages/Faq';
import Registry from '../../PublicWebsite/pages/Registry';
import Photos from '../../PublicWebsite/pages/Photos';
import Travel from '../../PublicWebsite/pages/Travel';
import Events from '../../PublicWebsite/pages/Events';
import Home from '../../PublicWebsite/pages/Home';
import Rsvp from '../../PublicWebsite/pages/Rsvp';
import PublicPagesNav from '../../PublicWebsite/PublicPagesNav';
import PublicHeader from '../../PublicWebsite/PublicHeader';
import PublicFooter from '../../PublicWebsite/PublicFooter';
import importImages from '../../../util/webpack';

const previewImages = importImages(require.context('../../../../public/images/icons/', false, /\.(jpg|jpeg|png|gif|svg)(\?.*)?$/));

const PAGE_COMPONENTS = {
  WEDDING_PARTY: WeddingParty,
  POI: Poi,
  FAQ: Faq,
  REGISTRY: Registry,
  PHOTO: Photos,
  TRAVEL: Travel,
  HOME: Home,
  EVENT: Events,
  RSVP: Rsvp
};

const formatLocation = (city, state) => {
  if (city && state) {
    return `${city}, ${state}`;
  } else if (city) {
    return city;
  } else if (state) {
    return state;
  }
  return undefined;
};

function PreviewOverlay({ copy, subcopy, buttonText, buttonAction }) {
  return (
    <div className="preview-hidden">
      <div className="preview-hidden-body">
        <i className="zolaicon zolaicon-lock" />
        <p className="large-cta">
          {copy}
        </p>
        <p className="margin-bottom-2x">
          {subcopy}
        </p>
        {buttonText && (
          <div>
            <button className="btn btn-primary btn-wide" onClick={buttonAction}>{buttonText}</button>
          </div>
        )}
      </div>
    </div>
  );
}

PreviewOverlay.propTypes = {
  copy: PropTypes.element,
  subcopy: PropTypes.element,
  buttonText: PropTypes.string,
  buttonAction: PropTypes.func,
};

const WebsitePreview = ({ activePage, values, pages, wedding, account, entities, guests }) => {
  let CurrentPage;
  if (activePage) {
    CurrentPage = PAGE_COMPONENTS[activePage.type];
  }
  const pageData = Object.assign({}, activePage, values);
  const weddingDate = moment.utc(wedding.wedding_date).isValid() ? moment.utc(wedding.wedding_date).format('MMMM D, YYYY') : undefined;

  const rsvpHidden = activePage && activePage.type === 'RSVP' && activePage.hidden;
  const noEvents = entities.orderedIds.length === 0;
  const noGuests = guests.orderedIds.length === 0;
  const numGuestsInvited = _.reduce(guests.orderedIds, (sum, guestGroupId) => {
    const guestGroup = guests.byId[guestGroupId];
    const eventInvitations = _.flatMap(guestGroup.guests, guest => guest.event_invitations);
    return sum + eventInvitations.length;
  }, 0);
  const noGuestsInvited = numGuestsInvited === 0;
  const rsvpDisabled = entities.orderedIds.filter(id => entities.byId[id].collect_rsvps === true).length === 0;

  let previewOverlay;
  if (rsvpDisabled) {
    previewOverlay = (
      <PreviewOverlay
        copy={<span>Want this page to be visible? Turn on online RSVPs for at least one of your events (and make sure guests have already been invited).</span>}
        subcopy={<span>To edit an event&#39;s RSVP settings, click on<br />the pencil icon to the right of the event name.</span>}
      />
    );
  }
  if (noGuestsInvited) {
    previewOverlay = (
      <PreviewOverlay
        copy={<span>Want this page to be visible?<br />Choose some guests to invite.</span>}
        buttonText="Invite Guests"
        buttonAction={() => browserHistory.push('/wedding/manage/guests/build')}
      />
    );
  }
  if (noGuests) {
    previewOverlay = (
      <PreviewOverlay
        copy={<span>Want this page to be visible?<br />First, build your Guest Directory.</span>}
        buttonText="Go To Guest Directory"
        buttonAction={() => browserHistory.push('/wedding/manage/guests')}
      />
    );
  }
  if (noEvents) {
    previewOverlay = (
      <PreviewOverlay
        copy={<span>Want guests to RSVP on your wedding website?</span>}
        subcopy={<span>First you must create an event, build a guest list for it, and enable online RSVP.</span>}
        buttonText="Create an Event"
        buttonAction={() => browserHistory.push('/wedding/manage/events')}
      />
    );
  }

  return (
    <div className="website-preview margin-top">
      <div className={`preview-container ${rsvpHidden && previewOverlay ? 'darken' : ''}`} id="public-wrapper">
        {activePage && activePage.hidden && (
          <div id="page-hidden-banner" className="text-center">
            <p><img src={previewImages('npHidden')} className="margin-right-2x" alt="Not Visible Icon" />This page is hidden, visible only to you.</p>
          </div>
        )}
        <div className={`preview-frame-container${rsvpHidden && previewOverlay ? ' darken' : ''}`}>
          <div className="preview-frame">
            <div className="sticky-nav">
              {wedding.slug && (
                <PublicHeader slug={wedding.slug} name={wedding.title} weddingDate={weddingDate} location={formatLocation(account.city, account.state_province)} hashtag={wedding.hashtag} />
              )}
              {wedding.slug && pages && (
                <PublicPagesNav slug={wedding.slug} pages={pages} />
              )}
            </div>
            <div id="public-page-content-wrapper">
              {CurrentPage && (
                <CurrentPage forPreview page={pageData} />
              )}
              {wedding.owner_first_name && wedding.partner_first_name && activePage && activePage.type !== 'HOME' && account.enable_seal && (
                <div className="container seal">
                  <div className="seal-section">
                    <span className="name-1">
                      {wedding.owner_first_name && (
                        <span className="initial-1">
                          {wedding.owner_first_name[0]}
                        </span>
                      )}
                      {wedding.owner_last_name && (
                        <span className="initial-2">
                          {wedding.owner_last_name[0]}
                        </span>
                      )}
                    </span>
                    {wedding.owner_first_name && wedding.partner_first_name && (
                      <span>
                        <span className="amp">&amp;</span>
                        <span className="and">and</span>
                        <span className="plus">+</span>
                      </span>
                    )}
                    <span className="name-2">
                      {wedding.partner_first_name && (
                        <span className="initial-1">
                          {wedding.partner_first_name[0]}
                        </span>
                      )}
                      {wedding.partner_last_name && (
                        <span className="initial-2">
                          {wedding.partner_last_name[0]}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
            {activePage && (
              <PublicFooter page={activePage} />
            )}
          </div>
        </div>
      </div>
      {rsvpHidden && previewOverlay && (
        <div>
          {previewOverlay}
        </div>
      )}
    </div>
  );
};

WebsitePreview.propTypes = {
  values: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    hidden: PropTypes.bool,
  }),
  activePage: PropTypes.shape({}),
  pages: PropTypes.arrayOf(PropTypes.shape({})),
  wedding: PropTypes.shape({}),
  account: PropTypes.shape({}),
  entities: PropTypes.shape({}),
  guests: PropTypes.shape({}),
};

const cmsPageFormSelector = formValueSelector('CmsPageForm');
const homePageFormSelector = formValueSelector('homepageSection');

const mapStateToProps = (state) => {
  const page = state.website.pages.byId[state.website.pages.activePage.id];
  return {
    values: page && page.type === 'HOME' ? homePageFormSelector(state, 'title', 'header_image_url', 'footer_image_url') : cmsPageFormSelector(state, 'title', 'description', 'hidden', 'header_image_url'),
    activePage: page,
    pages: state.website.pages.orderedIds.map(id => state.website.pages.byId[id]),
    wedding: state.wedding.wedding,
    account: state.wedding.account,
    entities: state.website.entities,
    guests: state.guestList.guests
  };
};

const WebsitePreviewContainer = connect(
  mapStateToProps,
)(WebsitePreview);

export default WebsitePreviewContainer;



/** WEBPACK FOOTER **
 ** ./src/components/manage/website/WebsitePreview.jsx
 **/